import React, { useState, useEffect } from 'react';
import {
  Button,
  Stack,
  Typography,
  Box,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  DialogActions,
  DialogContent,
  Divider,
  useTheme,
  Chip,
  Slide,
  Grow,
} from '@mui/material';
import { Add, Close, DeleteOutline, Remove, RemoveCircleOutline } from '@mui/icons-material';
import { Product } from '@domain/product/models/Product';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { container } from '@infrastructure/container';
import { useToast } from '@shared/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { BottomSheet } from '@shared/shell/bottomsheet/BottomSheet';
import { BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import { CreateProductFlow } from './CreateProductFlow';
import { CombinePartProductSelector } from './CombinePartProductSelector';
import { JSEditableInput } from '@shared/ui/common';
import { useProductUpdate } from '../../hooks/useProductUpdate';
import { useProducts } from '../../hooks/useProducts';

interface ProductComponentState {
  productId: string;
  quantity: number;
}

interface CombineProductsBottomSheetProps {
  selectedProducts: Product[];
  onSuccess?: () => void;
  onClose: () => void;
}

const CombineProductsBottomSheet: React.FC<CombineProductsBottomSheetProps> = ({
  selectedProducts,
  onSuccess,
  onClose,
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const [productComponents, setProductComponents] = useState<ProductComponentState[]>([]);
  const [selectedPartProduct, setSelectedPartProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailBottomSheet, setDetailBottomSheet] = useState<React.ReactNode>(null);

  const { addProductUsedIn } = useProductUpdate();
  const { bulkEditProducts } = useProducts();

  useEffect(() => {
    if (selectedProducts.length > 0) {
      setProductComponents(
        selectedProducts.map((p) => ({
          productId: p.id,
          quantity: 1,
        }))
      );
    }
  }, [selectedProducts]);

  const handleQuantityChange = (productId: string, quantity: number) => {
    setProductComponents((prev) =>
      prev.map((pc) =>
        pc.productId === productId ? { ...pc, quantity: Math.max(1, quantity) } : pc
      )
    );
  };

  const handleRemoveProduct = (productId: string) => {
    setProductComponents((prev) => prev.filter((pc) => pc.productId !== productId));
  };

  const handleCloseDetailBottomSheet = () => {
    setDetailBottomSheet(null);
  };

  const handleCreateNew = () => {
    setDetailBottomSheet(
      <CreateProductFlow
        onClose={handleCloseDetailBottomSheet}
        onSuccess={(product) => {
          setSelectedPartProduct(product);
          handleCloseDetailBottomSheet();
        }}
      />
    );
  };

  const handleSelectExisting = () => {
    if (productComponents.length === 0) return;
    setDetailBottomSheet(
      <CombinePartProductSelector
        handleClose={handleCloseDetailBottomSheet}
        bundleProductIds={new Set(productComponents.map((pc) => pc.productId))}
        handleFinish={(product) => {
          const wasInBundle = productComponents.some((pc) => pc.productId === product.id);
          if (wasInBundle) {
            setProductComponents((prev) => prev.filter((pc) => pc.productId !== product.id));
          }
          setSelectedPartProduct(product);
          handleCloseDetailBottomSheet();
        }}
      />
    );
  };

  const handleSubmit = async () => {
    if (!selectedPartProduct) {
      showError('Please select a product to use as a part (Create new or Select existing)');
      return;
    }
    setIsSubmitting(true);
    try {
      await addProductUsedIn({ productId: selectedPartProduct.id, data: productComponents.map((pc) => ({ productId: pc.productId, quantity: pc.quantity || 1 })) });
      await bulkEditProducts({ updateData: { purchasable: false }, filters: { productIds: productComponents.map((pc) => pc.productId) } });
      success('Products bundled successfully');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      onClose();
      onSuccess?.();
    } catch (err) {
      showError('Failed to combine products');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemovePartProduct = () => {
    setSelectedPartProduct(null);
  };

  const [removeProductId, setRemoveProductId] = useState<string | undefined>(undefined);

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">Convert Products to Bundles</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 0, p: 0 }}>
        <Stack flex={1} overflow="auto" spacing={0}>


          <Stack gap={4} p={4}>
            <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center" sx={{ border: 1, borderColor: theme.palette.primary.main, borderRadius: 1, p: 2 }}>

              {selectedPartProduct ? (
                <Stack direction="row" alignItems="center" spacing={1} flex={1}>
                  <Stack direction="row" alignItems="center" spacing={2} flex={1}>
                    <Avatar src={selectedPartProduct.image} variant="rounded" sx={{ width: 40, height: 40, border: 1, borderColor: 'divider' }} />
                    <Typography variant="subtitle2" color="text.primary">
                      {selectedPartProduct.title || selectedPartProduct.ean}
                    </Typography>
                  </Stack>
                  <Button variant="text" sx={{ whiteSpace: 'nowrap' }} startIcon={<Close />} onClick={handleRemovePartProduct}>Remove</Button>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center" flex={1}>
                  <Stack direction="column" alignItems="flex-start" flex={1} spacing={0}>
                    <Typography variant="body2" color="text.primary">
                      Select product used in bundles
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Select a product to be used as a part in the products below
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button variant="text" sx={{ whiteSpace: 'nowrap' }} startIcon={<Add />} onClick={handleCreateNew}>
                      Create new
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Button variant="text" sx={{ whiteSpace: 'nowrap' }} startIcon={<Add />} onClick={handleSelectExisting}>
                      Select existing
                    </Button>
                  </Stack>
                </Stack>
              )}


            </Stack>



            <Table size="small" stickyHeader>
              <TableHead sx={{ backgroundColor: theme.palette.background.default, top: 0 }}>
                <TableRow>
                  <TableCell sx={{ p: 2 }}>Product</TableCell>
                  <TableCell sx={{ p: 2}} align="right">
                    Number of items in bundle
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productComponents.map((pc) => {
                  const product = selectedProducts.find((p) => p.id === pc.productId);
                  if (!product) return null;
                  return (
                    <TableRow
                      key={product.id}
                      onMouseEnter={() => setRemoveProductId(product.id)}
                      onMouseLeave={() => setRemoveProductId(undefined)}
                      hover
                      sx={{
                        '& .delete-btn': { opacity: 0, transition: 'opacity 0.2s' },
                        '&:hover .delete-btn': { opacity: 1 },
                      }}
                    >
                      <TableCell sx={{ py: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>


                          <Avatar
                            src={product.image}
                            variant="rounded"
                            sx={{
                              width: 40,
                              height: 40,
                              border: 1,
                              borderColor: 'divider',
                            }}
                          />

                          <Typography variant="body2" flexGrow={1}>
                            {product.title || 'Untitled'}
                          </Typography>

                          <Chip
                            label='Bundle'
                            size="small"
                            color="primary"
                          />

                        </Stack>
                      </TableCell>
                      <TableCell align="right">

                        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} minWidth={180}>
                          <Grow in={removeProductId === product.id} mountOnEnter unmountOnExit>
                            <IconButton
                              className="delete-btn"
                              size="small"
                              onClick={() => handleRemoveProduct(product.id)}
                              aria-label="Remove product"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              <RemoveCircleOutline fontSize="small" color="error" />
                            </IconButton>
                          </Grow>

                          <JSEditableInput
                            value={pc.quantity}
                            onValueChange={(value) => handleQuantityChange(product.id, Math.max(1, parseInt(String(value), 10) || 1))}
                            outlined
                            suffix="items"
                            placeholder="1"
                            sx={{
                              width: '150px',
                            }}
                          />
                        </Stack>
                      </TableCell>

                    </TableRow>
                  );
                })}

                {productComponents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ p: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No products selected
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!selectedPartProduct || productComponents.length === 0 || isSubmitting}>
          Convert to Bundles
        </Button>
      </DialogActions>

      <BottomSheet
        open={Boolean(detailBottomSheet)}
        onClose={handleCloseDetailBottomSheet}
        size={BottomSheetSize.MD}
      >
        {detailBottomSheet}
      </BottomSheet>
    </>
  );
};

export default CombineProductsBottomSheet;
